import { useEffect, useMemo, useRef, useState } from "react"
import { Archive, ArrowRight, Check, ChevronRight, CircleAlert, ExternalLink, FileText, GitPullRequest, Image, Inbox as InboxIcon, Mic, MoreHorizontal, Paperclip, Presentation, SendHorizontal, Square, Star, X } from "lucide-react"
import { InboxIssueStatusMark } from "./InboxIssueStatusMark"
import { AgentActivityFeed } from "./AgentActivityFeed"
import { PullRequestWorkflowGraph } from "./PullRequestWorkflowGraph"
import {
  PersonaAvatar,
  actionPersonaUsers,
  contextActionKey,
  contextPersonaKey,
  personaCardEssence,
  personaDisplayName
} from "./ContextOverview"
import CredentialSetupModal from "./CredentialSetupModal"
import {
  inboxIssueStatusClassName,
  inboxIssueStatusLabel,
  inboxIssueStatusToastLabel,
  inboxIssueStatuses,
  isClosedInboxIssue,
  isClosedInboxIssueStatus
} from "../lib/inboxIssueStatus"
import { csrfToken } from "../lib/rails"
import { useDismissiblePopover } from "../lib/useDismissiblePopover"
import { LoadingOrb } from "./LoadingOrb"

const reportStateKeys = [
  "visual",
  "reportThumbnailPath",
  "reportStatus",
  "reportStartedAt",
  "reportFinishedAt",
  "reportError"
]
const localIssueStateKeys = [
  "comments",
  "commentCount",
  "pullRequest",
  ...reportStateKeys
]
const noPullRequestOutcomes = new Set([
  "ALREADY_FIXED",
  "CONFIGURATION_OR_USAGE_ERROR",
  "INSUFFICIENT_EVIDENCE",
  "DUPLICATE_OR_STALE_ISSUE"
])
const noPullRequestOutcomeLabels = {
  ALREADY_FIXED: "No pull request was needed.",
  CONFIGURATION_OR_USAGE_ERROR: "No code change was needed.",
  INSUFFICIENT_EVIDENCE: "No pull request was created.",
  DUPLICATE_OR_STALE_ISSUE: "No pull request was needed."
}

const severityRank = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Low: 3
}
const stackComponentLayerLabels = {
  "AI Coding": "AI Coding",
  "Repository User Interface": "Repository",
  "Repository App Logic": "Repository",
  "Repository Auth & Security": "Repository",
  Hosting: "Hosting",
  Database: "Database",
  Services: "Services",
  Monitor: "Monitor"
}

export function orderedInboxIssues(issues = []) {
  return [...issues]
    .sort((a, b) => {
      return (severityRank[a.severity] ?? 4) - (severityRank[b.severity] ?? 4) ||
        (a.position ?? 0) - (b.position ?? 0)
    })
}

export function mergeInboxIssuePayloads(currentIssues = [], incomingIssues = []) {
  const currentById = new Map(currentIssues.map((issue) => [ issue.id, issue ]))

  return incomingIssues.map((incomingIssue) => {
    const currentIssue = currentById.get(incomingIssue.id)

    return currentIssue ? mergeInboxIssueUpdate(currentIssue, incomingIssue) : incomingIssue
  })
}

export function mergeInboxIssueUpdate(currentIssue, updatedIssue, { preserveTransientState = true } = {}) {
  if (!currentIssue) return updatedIssue
  if (!updatedIssue) return currentIssue

  const nextIssue = { ...currentIssue, ...updatedIssue }

  if (preserveTransientState && shouldPreserveCurrentReportState(currentIssue, updatedIssue)) {
    reportStateKeys.forEach((key) => {
      nextIssue[key] = currentIssue[key]
    })
  }
  if (preserveTransientState && shouldPreserveCurrentPullRequestState(currentIssue, updatedIssue)) {
    nextIssue.pullRequest = currentIssue.pullRequest
  }
  if (nextIssue.pullRequest && currentIssue.pullRequest?.streamEvents?.length) {
    nextIssue.pullRequest = {
      ...nextIssue.pullRequest,
      streamEvents: mergePullRequestStreamEvents(currentIssue.pullRequest.streamEvents, nextIssue.pullRequest.streamEvents || []),
      streamNextSequence: Math.max(
        Number(currentIssue.pullRequest.streamNextSequence) || 0,
        Number(nextIssue.pullRequest.streamNextSequence) || 0
      )
    }
  }

  return nextIssue
}

export function shouldPreserveCurrentReportState(currentIssue, updatedIssue) {
  return (currentIssue?.reportStatus || "idle") === "generating" &&
    updatedIssue?.reportStatus === "idle"
}

export function shouldPreserveCurrentPullRequestState(currentIssue, updatedIssue) {
  const currentPullRequest = currentIssue?.pullRequest
  const updatedPullRequest = updatedIssue?.pullRequest
  const authoritativeNoPullRequestCompletion = noPullRequestOutcomes.has(updatedPullRequest?.outcome) &&
    timestampAtOrAfter(updatedPullRequest?.finishedAt, currentPullRequest?.startedAt)

  return currentPullRequest?.status === "generating" &&
    updatedPullRequest?.status === "idle" &&
    !authoritativeNoPullRequestCompletion
}

function timestampAtOrAfter(candidate, reference) {
  const candidateParts = timestampParts(candidate)
  const referenceParts = timestampParts(reference)
  if (!candidateParts || !referenceParts) return false
  if (candidateParts.milliseconds !== referenceParts.milliseconds) {
    return candidateParts.milliseconds > referenceParts.milliseconds
  }

  return candidateParts.submillisecondDigits >= referenceParts.submillisecondDigits
}

function timestampParts(value) {
  const milliseconds = Date.parse(value)
  if (!Number.isFinite(milliseconds)) return null

  const fractionalMatch = String(value).match(/\.(\d+)(?:Z|[+-]\d{2}:?\d{2})$/i)
  const fraction = (fractionalMatch?.[1] || "").padEnd(9, "0").slice(0, 9)
  return {
    milliseconds,
    submillisecondDigits: Number(fraction.slice(3))
  }
}

export function mergePullRequestStreamEvents(currentEvents = [], incomingEvents = []) {
  const eventsBySequence = new Map()

  ;[...currentEvents, ...incomingEvents].forEach((event) => {
    const sequence = Number(event?.sequence)
    if (!Number.isFinite(sequence) || sequence <= 0) return

    eventsBySequence.set(sequence, {
      ...event,
      sequence,
      key: event.key || `${sequence}-${event.type || event.raw_type || event.rawType || "event"}`
    })
  })

  return [...eventsBySequence.values()]
    .sort((a, b) => a.sequence - b.sequence)
    .reduce((events, event) => {
      const mergeKey = pullRequestStreamEventMergeKey(event)
      const previous = events.at(-1)
      if (mergeKey && previous && pullRequestStreamEventMergeKey(previous) === mergeKey) {
        events[events.length - 1] = {
          ...previous,
          ...event,
          key: previous.key,
          mergeKey,
          merge_key: mergeKey,
          sequence: Math.max(previous.sequence, event.sequence),
          text: `${previous.text || ""}${event.text || ""}`
        }
        return events
      }

      events.push(event)
      return events
    }, [])
}

function pullRequestStreamEventMergeKey(event) {
  return event?.mergeKey || event?.merge_key || null
}

export function pullRequestTranscriptLines(events = [], { status = "Pull request generation log." } = {}) {
  if (events.length === 0) {
    return status ? [
      { key: "status", text: status, tone: "status" }
    ] : []
  }

  return [...events]
    .sort((a, b) => Number(a?.sequence || 0) - Number(b?.sequence || 0))
    .map((event, index) => {
      const sequence = Number(event?.sequence)
      const keyPrefix = Number.isFinite(sequence) && sequence > 0 ? sequence : index
      const type = event?.type || event?.raw_type || event?.rawType || "status"

      return {
        key: event?.key || `${keyPrefix}-${type}`,
        text: event?.text || event?.raw_type || event?.rawType || "",
        tone: pullRequestTranscriptTone(type)
      }
    })
}

function pullRequestTranscriptTone(type) {
  const tone = String(type || "status").toLowerCase()
  if (tone === "activity") return "status"
  if ([ "done", "error", "reasoning", "status" ].includes(tone)) return tone
  return "status"
}

export function lastPullRequestStreamSequence(events = [], fallback = 0) {
  return events.reduce((lastSequence, event) => {
    const sequence = Number(event?.sequence)
    return Number.isFinite(sequence) && sequence > lastSequence ? sequence : lastSequence
  }, Number(fallback) || 0)
}

export function pullRequestStreamPayloadNextSequence(payload, events = [], fallback = 0) {
  if (payload?.next_sequence !== undefined && payload.next_sequence !== null) {
    const nextSequence = Number(payload.next_sequence)
    if (Number.isFinite(nextSequence)) return nextSequence
  }

  return lastPullRequestStreamSequence(events, fallback)
}

function inboxIssueWithLocalState(issueToUpdate, currentStates = {}, sourceIssues = []) {
  const sourceIssue = sourceIssues.find((candidate) => String(candidate.id) === String(issueToUpdate?.id)) || issueToUpdate
  const issueId = sourceIssue?.id ?? issueToUpdate?.id

  return {
    ...sourceIssue,
    ...(currentStates[issueId] || currentStates[issueToUpdate?.id] || {})
  }
}

export function mergePullRequestStreamLocalState(issueToUpdate, payload, currentStates = {}, sourceIssues = []) {
  const incomingEvents = Array.isArray(payload?.events) ? payload.events : []
  const currentIssue = inboxIssueWithLocalState(issueToUpdate, currentStates, sourceIssues)
  const issueId = currentIssue?.id ?? issueToUpdate?.id
  const pullRequest = currentIssue?.pullRequest || issueToUpdate?.pullRequest || {}
  const streamEvents = mergePullRequestStreamEvents(pullRequest.streamEvents || [], incomingEvents)
  const nextSequence = pullRequestStreamPayloadNextSequence(payload, streamEvents, pullRequest.streamNextSequence)

  if (incomingEvents.length === 0) {
    return {
      changed: false,
      issueId,
      nextSequence,
      nextStates: currentStates
    }
  }

  const nextIssueState = {
    ...(currentStates[issueId] || {}),
    pullRequest: {
      ...pullRequest,
      streamEvents,
      streamNextSequence: nextSequence
    }
  }

  if (localIssueStateEqual(currentStates[issueId], nextIssueState)) {
    return {
      changed: false,
      issueId,
      nextSequence,
      nextStates: currentStates
    }
  }

  return {
    changed: true,
    issueId,
    nextSequence,
    nextStates: {
      ...currentStates,
      [issueId]: nextIssueState
    }
  }
}

const automaticRetryPollingGrace = 30_000

export function pullRequestAutomaticRetryPending(pullRequest = {}, now = Date.now()) {
  if (!pullRequest) return false

  const retryUntil = Date.parse(pullRequest.automaticRetryUntil)
  return pullRequest.status === "failed" &&
    Number.isFinite(retryUntil) &&
    retryUntil + automaticRetryPollingGrace > now
}

export function pullRequestPollingActive(pullRequest = {}, now = Date.now()) {
  if (!pullRequest) return false

  return pullRequest.status === "generating" || pullRequestAutomaticRetryPending(pullRequest, now)
}

export function generatingInboxPullRequestIssues(issues = [], now = Date.now()) {
  return issues.filter((inboxIssue) => (
    inboxIssue.pullRequest?.statusPath &&
    pullRequestPollingActive(inboxIssue.pullRequest, now)
  ))
}

export function generatingInboxReportIssues(issues = []) {
  return issues.filter((inboxIssue) => inboxIssue.reportPath && (
    inboxIssue.reportStatus === "generating" || inboxIssue.visual?.status === "generating"
  ))
}

export function pullRequestGenerationRequest(issue, authenticityToken = csrfToken()) {
  const createPath = issue?.pullRequest?.createPath
  if (!createPath) return null

  return {
    path: createPath,
    options: {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": authenticityToken
      }
    }
  }
}

export async function pullRequestResponsePayload(response, fallbackMessage) {
  let payload

  try {
    payload = await response.json()
  } catch {
    throw new Error(fallbackMessage)
  }

  if (!response.ok) {
    throw Object.assign(new Error(payload?.error || fallbackMessage), { payload })
  }

  return payload
}

export function pullRequestStopRequest(issue, authenticityToken = csrfToken()) {
  const stopPath = issue?.pullRequest?.stopPath
  if (!stopPath) return null

  return {
    path: stopPath,
    options: {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": authenticityToken
      }
    }
  }
}

export function pullRequestRetryPublicationRequest(issue, authenticityToken = csrfToken()) {
  const retryPublishPath = issue?.pullRequest?.retryPublishPath
  if (!retryPublishPath) return null

  return {
    path: retryPublishPath,
    options: {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": authenticityToken
      }
    }
  }
}

export function optimisticPullRequestGenerationUpdate(issue, startedAt = new Date().toISOString()) {
  const pullRequest = issue?.pullRequest
  if (!pullRequest) return null

  return {
    pullRequest: {
      ...pullRequest,
      status: "generating",
      startedAt,
      finishedAt: null,
      error: null,
      outcome: null,
      diagnosis: null,
      eventsPath: null,
      agentRunPath: null,
      stopPath: null,
      streamEvents: [],
      streamNextSequence: 0
    }
  }
}

export function pullRequestActionState(pullRequest = {}, canGenerateFromHere = true, now = Date.now()) {
  const status = pullRequest.status || "idle"
  const ready = status === "ready" && Boolean(pullRequest.url)
  const generating = pullRequestPollingActive(pullRequest, now)
  const retryPublication = !generating && Boolean(pullRequest.retryPublishPath)

  return {
    status,
    ready,
    generating,
    retryPublication,
    disabled: generating || (!retryPublication && (!pullRequest.canGenerate || !canGenerateFromHere))
  }
}

export function pullRequestTimingLabel(pullRequest = {}, events = [], now = Date.now()) {
  const startedAt = Date.parse(pullRequest.startedAt)
  if (!Number.isFinite(startedAt)) return ""

  const finishedAt = Date.parse(pullRequest.finishedAt)
  const endAt = Number.isFinite(finishedAt) ? finishedAt : now
  const elapsedSeconds = Math.max(0, Math.floor((endAt - startedAt) / 1000))
  const latestEvent = [...events].sort((a, b) => Number(a?.sequence || 0) - Number(b?.sequence || 0)).at(-1)
  const latestAt = Date.parse(latestEvent?.occurred_at || latestEvent?.occurredAt || pullRequest.startedAt)
  const activitySeconds = Number.isFinite(latestAt) ? Math.max(0, Math.floor((now - latestAt) / 1000)) : null
  const activityTimestamp = Number.isFinite(latestAt)
    ? `${new Date(latestAt).toISOString().slice(0, 19).replace("T", " ")} UTC`
    : null
  const elapsed = elapsedSeconds >= 60
    ? `${Math.floor(elapsedSeconds / 60)}m ${elapsedSeconds % 60}s`
    : `${elapsedSeconds}s`
  const activity = activitySeconds == null || !activityTimestamp
    ? "Last activity unavailable"
    : activitySeconds < 2
      ? `Last activity now (${activityTimestamp})`
      : `Last activity ${activitySeconds}s ago (${activityTimestamp})`

  return `Elapsed ${elapsed} · ${activity}`
}

export function pullRequestStatusRequest(issue) {
  const statusPath = issue?.pullRequest?.statusPath
  if (!statusPath) return null

  return {
    path: statusPath,
    options: {
      headers: { Accept: "application/json" }
    }
  }
}

export function pullRequestEventsRequest(issue, after = 0) {
  const eventsPath = issue?.pullRequest?.eventsPath
  if (!eventsPath) return null

  const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin
  const url = new URL(eventsPath, origin)
  url.searchParams.set("after", Number(after) || 0)

  return {
    path: url.toString(),
    options: {
      cache: "no-store",
      headers: {
        Accept: "application/json",
        "Cache-Control": "no-store"
      }
    }
  }
}

export async function fetchPullRequestEventPages(issue, { after = 0, fetcher = fetch, onPage } = {}) {
  let cursor = Number(after) || 0

  while (true) {
    const request = pullRequestEventsRequest(issue, cursor)
    if (!request) return cursor

    const response = await fetcher(request.path, request.options)
    if (!response.ok) throw new Error("Could not read pull request generation events.")

    const payload = await response.json()
    if (onPage?.(payload, cursor) === false) return cursor

    const events = Array.isArray(payload?.events) ? payload.events : []
    const nextSequence = pullRequestStreamPayloadNextSequence(payload, events, cursor)
    if (nextSequence <= cursor) return cursor

    cursor = nextSequence
  }
}

function pullRequestStreamKey(issue) {
  const pullRequest = issue?.pullRequest || {}
  return [
    issue?.id || "issue",
    pullRequest.agentRunPath || pullRequest.startedAt || pullRequest.eventsPath || "pull-request"
  ].join(":")
}

export function Inbox({
  readOnly = false,
  canComment = false,
  concept = null,
  contextActionListPath = null,
  contextOverview = null,
  contextPersonaListPath = null,
  contextStoryboards = [],
  issue,
  issues,
  mentionableMembers = [],
  onIssueNavigate,
  onIssueStatusToast,
  onIssueUpdate,
  onListNavigate,
  selectedIssueKey
}) {
  const recordingRef = useRef(null)
  const pullRequestStreamSequencesRef = useRef({})
  const [localIssueStates, setLocalIssueStates] = useState({})
  const [commentActionStates, setCommentActionStates] = useState({})
  const [commentAttachments, setCommentAttachments] = useState({})
  const [commentDrafts, setCommentDrafts] = useState({})
  const [commentRecorderStates, setCommentRecorderStates] = useState({})
  const [pullRequestPollingInterruptions, setPullRequestPollingInterruptions] = useState({})
  const [pullRequestPublicationRetries, setPullRequestPublicationRetries] = useState({})
  const sourceInboxIssues = useMemo(() => (
    orderedInboxIssues(issues?.length ? issues : [issue].filter(Boolean))
  ), [issue, issues])
  const sourceInboxIssuesRef = useRef(sourceInboxIssues)
  const localIssueStatesRef = useRef(localIssueStates)
  const inboxIssues = useMemo(() => (
    sourceInboxIssues.map((inboxIssue) => ({ ...inboxIssue, ...(localIssueStates[inboxIssue.id] || {}) }))
  ), [sourceInboxIssues, localIssueStates])
  const selectedIssue = selectedIssueKey
    ? inboxIssues.find((inboxIssue) => (
      inboxIssue.key === selectedIssueKey || String(inboxIssue.id) === String(selectedIssueKey)
    )) || null
    : issue || null
  const selectedCommentActionState = selectedIssue ? commentActionStates[selectedIssue.id] || {} : {}
  const selectedCommentAttachments = selectedIssue ? commentAttachments[selectedIssue.id] || [] : []
  const selectedCommentDraft = selectedIssue ? commentDrafts[selectedIssue.id] || "" : ""
  const selectedCommentRecorderState = selectedIssue ? commentRecorderStates[selectedIssue.id] || {} : {}
  const generatingReportIssues = useMemo(() => (
    generatingInboxReportIssues(inboxIssues)
  ), [inboxIssues])
  const generatingPullRequestIssues = useMemo(() => generatingInboxPullRequestIssues(inboxIssues), [inboxIssues])
  const streamingPullRequestIssues = useMemo(() => (
    inboxIssues.filter((inboxIssue) => (
      inboxIssue.pullRequest?.eventsPath &&
      pullRequestPollingActive(inboxIssue.pullRequest)
    ))
  ), [inboxIssues])
  const transcribingCommentIssues = useMemo(() => (
    inboxIssues.filter((inboxIssue) => (
      inboxIssue.commentsPath &&
      (inboxIssue.comments || []).some((comment) => comment.voiceTranscriptionStatus === "transcribing")
    ))
  ), [inboxIssues])
  const generatingReportKey = generatingReportIssues
    .map((inboxIssue) => `${inboxIssue.id}:${inboxIssue.reportPath}`)
    .join("|")
  const generatingPullRequestKey = generatingPullRequestIssues
    .map((inboxIssue) => `${inboxIssue.id}:${inboxIssue.pullRequest?.statusPath}`)
    .join("|")
  const streamingPullRequestKey = streamingPullRequestIssues
    .map((inboxIssue) => pullRequestStreamKey(inboxIssue))
    .join("|")
  const selectedPullRequestReplayKey = selectedIssue?.pullRequest?.eventsPath &&
    !pullRequestPollingActive(selectedIssue.pullRequest)
    ? pullRequestStreamKey(selectedIssue)
    : ""
  const transcribingCommentKey = transcribingCommentIssues
    .map((inboxIssue) => `${inboxIssue.id}:${(inboxIssue.comments || []).map((comment) => `${comment.id}:${comment.voiceTranscriptionStatus}`).join(",")}`)
    .join("|")

  useEffect(() => {
    setLocalIssueStates((current) => {
      const nextStates = pruneLocalIssueStates(current, sourceInboxIssues)
      localIssueStatesRef.current = nextStates

      return nextStates
    })
  }, [sourceInboxIssues])

  function setPullRequestPollingInterrupted(issueToUpdate, interrupted) {
    if (!issueToUpdate?.id) return

    setPullRequestPollingInterruptions((current) => {
      if (Boolean(current[issueToUpdate.id]) === interrupted) return current
      if (interrupted) return { ...current, [issueToUpdate.id]: true }

      const next = { ...current }
      delete next[issueToUpdate.id]
      return next
    })
  }

  useEffect(() => {
    sourceInboxIssuesRef.current = sourceInboxIssues
  }, [sourceInboxIssues])

  useEffect(() => {
    localIssueStatesRef.current = localIssueStates
  }, [localIssueStates])

  useEffect(() => {
    return () => {
      stopRecordingTracks(recordingRef.current)
    }
  }, [])

  useEffect(() => {
    if (generatingReportIssues.length === 0) return undefined

    let cancelled = false

    function pollReports() {
      generatingReportIssues.forEach((issueToPoll) => {
        fetch(issueToPoll.reportPath, {
          headers: { Accept: "application/json" }
        })
          .then((response) => {
            if (!response.ok) throw new Error("Could not check the issue report.")
            return response.json()
          })
          .then((updatedIssue) => {
            if (!cancelled) applyIssueUpdate(issueToPoll, updatedIssue)
          })
          .catch(() => {})
      })
    }

    pollReports()
    const interval = window.setInterval(pollReports, 10000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [generatingReportKey])

  useEffect(() => {
    if (transcribingCommentIssues.length === 0) return undefined

    let cancelled = false

    function pollComments() {
      transcribingCommentIssues.forEach((issueToPoll) => {
        fetch(issueToPoll.commentsPath, {
          headers: { Accept: "application/json" }
        })
          .then((response) => {
            if (!response.ok) throw new Error("Could not check the comment attachments.")
            return response.json()
          })
          .then((updatedIssue) => {
            if (!cancelled) applyIssueUpdate(issueToPoll, updatedIssue)
          })
          .catch(() => {})
      })
    }

    pollComments()
    const interval = window.setInterval(pollComments, 8000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [transcribingCommentKey])

  useEffect(() => {
    if (streamingPullRequestIssues.length === 0) return undefined

    let cancelled = false

    function pollPullRequestEvents() {
      streamingPullRequestIssues.forEach((issueToPoll) => {
        const streamKey = pullRequestStreamKey(issueToPoll)
        const pullRequest = issueToPoll.pullRequest || {}
        const after = pullRequestStreamSequencesRef.current[streamKey] ??
          pullRequest.streamNextSequence ??
          lastPullRequestStreamSequence(pullRequest.streamEvents || [])
        const request = pullRequestEventsRequest(issueToPoll, after)
        if (!request) return

        fetch(request.path, request.options)
          .then((response) => {
            if (!response.ok) throw new Error("Could not read pull request generation events.")
            return response.json()
          })
          .then((payload) => {
            if (cancelled) return

            setPullRequestPollingInterrupted(issueToPoll, false)
            applyPullRequestStreamPayload(issueToPoll, payload, streamKey)
          })
          .catch(() => {
            if (!cancelled) setPullRequestPollingInterrupted(issueToPoll, true)
          })
      })
    }

    pollPullRequestEvents()
    const interval = window.setInterval(pollPullRequestEvents, 1000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [streamingPullRequestKey])

  useEffect(() => {
    if (!selectedPullRequestReplayKey || !selectedIssue?.pullRequest?.eventsPath) return undefined

    let cancelled = false
    const pullRequest = selectedIssue.pullRequest || {}
    const after = pullRequestStreamSequencesRef.current[selectedPullRequestReplayKey] ??
      pullRequest.streamNextSequence ??
      lastPullRequestStreamSequence(pullRequest.streamEvents || [])

    fetchPullRequestEventPages(selectedIssue, {
      after,
      onPage: (payload) => {
        if (cancelled) return false
        setPullRequestPollingInterrupted(selectedIssue, false)
        applyPullRequestStreamPayload(selectedIssue, payload, selectedPullRequestReplayKey)
        return true
      }
    })
      .catch(() => {
        if (!cancelled) setPullRequestPollingInterrupted(selectedIssue, true)
      })

    return () => {
      cancelled = true
    }
  }, [selectedPullRequestReplayKey])

  useEffect(() => {
    if (generatingPullRequestIssues.length === 0) return undefined

    let cancelled = false

    function pollPullRequests() {
      generatingPullRequestIssues.forEach((issueToPoll) => {
        const request = pullRequestStatusRequest(issueToPoll)
        if (!request) return

        fetch(request.path, request.options)
          .then((response) => {
            if (!response.ok) throw new Error("Could not check pull request generation.")
            return response.json()
          })
          .then((updatedIssue) => {
            if (!cancelled) {
              setPullRequestPollingInterrupted(issueToPoll, false)
              applyIssueUpdate(issueToPoll, updatedIssue)
            }
          })
          .catch(() => {
            if (!cancelled) setPullRequestPollingInterrupted(issueToPoll, true)
          })
      })
    }

    pollPullRequests()
    const interval = window.setInterval(pollPullRequests, 10000)

    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [generatingPullRequestKey])

  const issueMetadataText = issueDetailMetadata(selectedIssue)
  const selectedIssueReportReady = (selectedIssue?.reportStatus || "idle") === "ready"
  const relatedArtifacts = useMemo(() => issueRelatedArtifacts(selectedIssue, {
    concept,
    contextActionListPath,
    contextOverview,
    contextPersonaListPath,
    contextStoryboards
  }), [concept, contextActionListPath, contextOverview, contextPersonaListPath, contextStoryboards, selectedIssue])

  if (!selectedIssue) return null

  function updateStatus(issueToUpdate, status, { publishOptimistic = false } = {}) {
    const previous = issueToUpdate
    // Keep closures local until the PATCH confirms; Apply publishes its pending state for the detail handoff.
    applyIssueUpdate(issueToUpdate, { status }, {
      localKeys: [ "status" ],
      publish: publishOptimistic
    })

    fetch(issueToUpdate.updatePath, {
      method: "PATCH",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: JSON.stringify({ repository_inbox_issue: { status } })
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not update inbox issue.")
        return response.json()
      })
      .then((updatedIssue) => {
        applyIssueUpdate(issueToUpdate, updatedIssue, { clearLocalKeys: [ "status" ] })

        if (isClosedInboxIssueStatus(status)) {
          onListNavigate?.()
          onIssueStatusToast?.(`Item was marked as ${inboxIssueStatusToastLabel(status)}.`)
        }
      })
      .catch(() => applyIssueUpdate(issueToUpdate, { status: previous.status }, {
        clearLocalKeys: [ "status" ],
        publish: publishOptimistic
      }))
  }

  function startReportGeneration(issueToUpdate) {
    applyIssueUpdate(issueToUpdate, {
      reportStatus: "generating",
      reportStartedAt: new Date().toISOString(),
      reportFinishedAt: null,
      reportError: null
    })

    fetch(issueToUpdate.reportPath, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error("Could not start the issue report.")
        return response.json()
      })
      .then((updatedIssue) => {
        if (!updatedIssue?.reportStatus || updatedIssue.reportStatus === "idle") return
        applyIssueUpdate(issueToUpdate, updatedIssue)
      })
      .catch(() => {
        applyIssueUpdate(issueToUpdate, {
          reportStatus: "failed",
          reportError: "Could not start the detailed issue report."
        })
      })
  }

  function startPullRequestGeneration(issueToUpdate) {
    const pullRequest = issueToUpdate.pullRequest
    const request = pullRequestGenerationRequest(issueToUpdate)
    if (!pullRequest?.createPath || !request) return

    applyIssueUpdate(issueToUpdate, optimisticPullRequestGenerationUpdate(issueToUpdate))

    fetch(request.path, request.options)
      .then((response) => pullRequestResponsePayload(response, "Could not start pull request generation."))
      .then((payload) => {
        applyIssueUpdate(issueToUpdate, payload.issue || payload)
      })
      .catch((error) => {
        if (error.payload?.issue) {
          applyIssueUpdate(issueToUpdate, error.payload.issue, { preserveTransientState: false })
          return
        }

        applyIssueUpdate(issueToUpdate, {
          pullRequest: {
            ...pullRequest,
            status: "failed",
            error: error.message || "Could not start pull request generation."
          }
        })
      })
  }

  function stopPullRequestGeneration(issueToUpdate) {
    const request = pullRequestStopRequest(issueToUpdate)
    if (!request) return

    fetch(request.path, request.options)
      .then((response) => pullRequestResponsePayload(response, "Could not stop pull request generation."))
      .then((payload) => {
        applyIssueUpdate(issueToUpdate, payload.issue || payload, { preserveTransientState: false })
      })
      .catch((error) => {
        applyIssueUpdate(issueToUpdate, {
          pullRequest: {
            ...issueToUpdate.pullRequest,
            error: error.message || "Could not stop pull request generation."
          }
        })
      })
  }

  function retryPullRequestPublication(issueToUpdate) {
    const request = pullRequestRetryPublicationRequest(issueToUpdate)
    if (!request) return

    setPullRequestPublicationRetries((current) => ({ ...current, [issueToUpdate.id]: true }))
    fetch(request.path, request.options)
      .then((response) => pullRequestResponsePayload(response, "Could not retry pull request publication."))
      .then((payload) => {
        applyIssueUpdate(issueToUpdate, payload.issue || payload, { preserveTransientState: false })
      })
      .catch((error) => {
        if (error.payload?.issue) {
          applyIssueUpdate(issueToUpdate, error.payload.issue, { preserveTransientState: false })
          return
        }

        applyIssueUpdate(issueToUpdate, {
          pullRequest: {
            ...issueToUpdate.pullRequest,
            error: error.message || "Could not retry pull request publication."
          }
        })
      })
      .finally(() => {
        setPullRequestPublicationRetries((current) => {
          const next = { ...current }
          delete next[issueToUpdate.id]
          return next
        })
      })
  }

  async function refetchIssuePullRequestProps(issueToRefresh) {
    const request = pullRequestStatusRequest(issueToRefresh)
    if (!request) return

    try {
      const response = await fetch(request.path, request.options)
      if (!response.ok) return
      const payload = await response.json()
      applyIssueUpdate(issueToRefresh, payload)
    } catch {
      // A stale pull request state is acceptable; the pollers will refresh it.
    }
  }

  function applyPullRequestStreamPayload(issueToUpdate, payload, streamKey = pullRequestStreamKey(issueToUpdate)) {
    const incomingEvents = Array.isArray(payload?.events) ? payload.events : []

    if (incomingEvents.length === 0 && payload?.next_sequence === undefined) return

    setLocalIssueStates((current) => {
      const result = mergePullRequestStreamLocalState(issueToUpdate, payload, current, sourceInboxIssuesRef.current)
      pullRequestStreamSequencesRef.current[streamKey] = result.nextSequence
      localIssueStatesRef.current = result.nextStates

      return result.nextStates
    })
  }

  function applyIssueUpdate(issueToUpdate, updatedIssue, { clearLocalKeys = [], localKeys = [], preserveTransientState = true, publish = true } = {}) {
    const currentIssue = inboxIssueWithLocalState(issueToUpdate, localIssueStatesRef.current, sourceInboxIssuesRef.current)
    const nextIssue = mergeInboxIssueUpdate(currentIssue, updatedIssue, { preserveTransientState })
    const nextLocalState = issueLocalStateFromUpdate(updatedIssue, nextIssue, { keys: localKeys })

    if (Object.keys(nextLocalState).length > 0 || clearLocalKeys.length > 0) {
      setLocalIssueStates((current) => {
        const stateCurrentIssue = inboxIssueWithLocalState(issueToUpdate, current, sourceInboxIssuesRef.current)
        const stateNextIssue = mergeInboxIssueUpdate(stateCurrentIssue, updatedIssue, { preserveTransientState })
        const stateNextLocalState = issueLocalStateFromUpdate(updatedIssue, stateNextIssue, { keys: localKeys })
        const nextIssueState = {
          ...(current[issueToUpdate.id] || {}),
          ...stateNextLocalState
        }
        clearLocalKeys.forEach((key) => delete nextIssueState[key])

        if (Object.keys(nextIssueState).length === 0) {
          if (!current[issueToUpdate.id]) return current

          const nextStates = { ...current }
          delete nextStates[issueToUpdate.id]
          localIssueStatesRef.current = nextStates

          return nextStates
        }
        if (localIssueStateEqual(current[issueToUpdate.id], nextIssueState)) return current

        const nextStates = {
          ...current,
          [issueToUpdate.id]: nextIssueState
        }
        localIssueStatesRef.current = nextStates

        return nextStates
      })
    }
    if (publish) onIssueUpdate?.(nextIssue)
  }

  function setCommentRecorderState(issueId, nextState) {
    setCommentRecorderStates((current) => ({
      ...current,
      [issueId]: { ...(current[issueId] || {}), ...nextState }
    }))
  }

  function setCommentActionState(issueId, nextState) {
    setCommentActionStates((current) => ({
      ...current,
      [issueId]: { ...(current[issueId] || {}), ...nextState }
    }))
  }

  function setCommentDraft(issueId, value) {
    setCommentDrafts((current) => ({
      ...current,
      [issueId]: value
    }))
  }

  function addCommentAttachments(issueId, entries) {
    setCommentAttachments((current) => ({
      ...current,
      [issueId]: [...(current[issueId] || []), ...entries]
    }))
  }

  function removeCommentAttachment(issueId, attachmentId) {
    setCommentAttachments((current) => ({
      ...current,
      [issueId]: (current[issueId] || []).filter((attachment) => attachment.id !== attachmentId)
    }))
  }

  function createComment(issueToUpdate, event) {
    event.preventDefault()

    const body = (commentDrafts[issueToUpdate.id] || "").trim()
    const attachments = commentAttachments[issueToUpdate.id] || []
    if (!body && attachments.length === 0) return

    setCommentActionState(issueToUpdate.id, { status: "submitting", error: null })

    const formData = new FormData()
    formData.append("repository_inbox_issue_comment[body]", body)
    if (attachments.some((attachment) => attachment.source === "voice")) {
      formData.append("repository_inbox_issue_comment[kind]", "voice_note")
    }
    attachments.forEach((attachment) => {
      formData.append("repository_inbox_issue_comment[attachments][]", attachment.file, attachment.name)
    })

    fetch(issueToUpdate.commentsPath, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: formData
    })
      .then((response) => response.json().then((payload) => ({ payload, response })))
      .then(({ payload, response }) => {
        if (!response.ok) throw new Error(payload.error || "Could not add the comment.")

        const nextComments = [...(issueToUpdate.comments || []), payload.comment].filter(Boolean)
        applyIssueUpdate(issueToUpdate, payload.issue || {
          comments: nextComments,
          commentCount: nextComments.length
        })
        setCommentDraft(issueToUpdate.id, "")
        setCommentAttachments((current) => ({
          ...current,
          [issueToUpdate.id]: []
        }))
        setCommentActionState(issueToUpdate.id, { status: "idle", error: null })
      })
      .catch((error) => {
        setCommentActionState(issueToUpdate.id, {
          status: "failed",
          error: error.message || "Could not add the comment."
        })
      })
  }

  async function startVoiceRecording(issueToUpdate) {
    if (recordingRef.current) {
      setCommentRecorderState(issueToUpdate.id, {
        status: "failed",
        error: "Stop the current recording before starting another."
      })
      return
    }

    if (!browserRecordingSupported()) {
      setCommentRecorderState(issueToUpdate.id, {
        status: "failed",
        error: "This browser cannot record audio here. Upload an audio file instead."
      })
      return
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = preferredRecorderMimeType()
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
      const recording = { chunks: [], issue: issueToUpdate, recorder, stream }
      recordingRef.current = recording

      recorder.addEventListener("dataavailable", (event) => {
        if (event.data?.size) recording.chunks.push(event.data)
      })

      recorder.addEventListener("stop", () => {
        stopRecordingTracks(recording)
        const blob = new Blob(recording.chunks, { type: recorder.mimeType || "audio/webm" })
        recordingRef.current = null
        addCommentAttachments(issueToUpdate.id, [commentAttachmentEntry(recordedVoiceFile(blob), "voice")])
        setCommentRecorderState(issueToUpdate.id, { status: "idle", error: null })
      }, { once: true })

      recorder.start()
      setCommentRecorderState(issueToUpdate.id, { status: "recording", error: null })
    } catch (_error) {
      setCommentRecorderState(issueToUpdate.id, {
        status: "failed",
        error: "Could not start recording. Upload an audio file instead."
      })
      stopRecordingTracks(recordingRef.current)
      recordingRef.current = null
    }
  }

  function stopVoiceRecording(issueToUpdate) {
    const recording = recordingRef.current
    if (!recording || recording.issue.id !== issueToUpdate.id) return
    if (recording.recorder.state === "inactive") return

    setCommentRecorderState(issueToUpdate.id, { status: "stopping", error: null })
    recording.recorder.stop()
  }

  function attachCommentFiles(issueToUpdate, event) {
    const files = Array.from(event.target.files || [])
    event.target.value = ""
    if (files.length === 0) return

    addCommentAttachments(issueToUpdate.id, files.map((file) => commentAttachmentEntry(file, "file")))
  }

  const detailPanel = !selectedIssue
    ? null
    : (
      <IssueDetailPanel
        onClose={onListNavigate}
        statusMenu={readOnly ? null : <IssueDetailStatusMenu issue={selectedIssue} onStatusChange={updateStatus} />}
        reportAction={selectedIssue.reportPath && selectedIssueReportReady ? <IssueReportAction issue={selectedIssue} /> : null}
      >
        <div className="issue-detail-layout">
          <div className="issue-detail-stack">
            <IssueCard
              issue={selectedIssue}
              metadata={issueMetadataText}
            />

            {!readOnly ? <div className="issue-hero-actions">
              <div className="issue-hero-action-row">
                <IssuePullRequestAction
                  issue={selectedIssue}
                  onGenerate={startPullRequestGeneration}
                  onRetryPublication={retryPullRequestPublication}
                  onStop={stopPullRequestGeneration}
                  onCredentialsReady={() => refetchIssuePullRequestProps(selectedIssue)}
                  pollingInterrupted={Boolean(pullRequestPollingInterruptions[selectedIssue.id])}
                  publicationRetrying={Boolean(pullRequestPublicationRetries[selectedIssue.id])}
                />
              </div>
            </div> : null}

            <IssueRelatedArtifacts relatedArtifacts={relatedArtifacts} />
          </div>

          <div className="issue-detail-rail">
            {!readOnly && selectedIssue.reportPath && !selectedIssueReportReady ? (
              <IssueReportAction issue={selectedIssue} onGenerate={startReportGeneration} />
            ) : null}

            <IssueComments
              actionState={selectedCommentActionState}
              attachments={selectedCommentAttachments}
              canComment={!readOnly && canComment && Boolean(selectedIssue.commentsPath)}
              draft={selectedCommentDraft}
              issue={selectedIssue}
              mentionableMembers={mentionableMembers}
              onAttachFiles={(event) => attachCommentFiles(selectedIssue, event)}
              onDraftChange={(value) => setCommentDraft(selectedIssue.id, value)}
              onRemoveAttachment={(attachmentId) => removeCommentAttachment(selectedIssue.id, attachmentId)}
              onStartRecording={() => startVoiceRecording(selectedIssue)}
              onStopRecording={() => stopVoiceRecording(selectedIssue)}
              onSubmit={(event) => createComment(selectedIssue, event)}
              recorderState={selectedCommentRecorderState}
            />
          </div>
        </div>
      </IssueDetailPanel>
    )

  return detailPanel
}

// Floating chrome for the issue detail: fixed right panel on desktop, bottom
// sheet on mobile. The corner controls stay visible while the body scrolls.
function IssueDetailPanel({ children, onClose, reportAction, statusMenu }) {
  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.defaultPrevented || event.key !== "Escape") return

      onClose?.()
    }

    window.addEventListener("keydown", handleKeydown)

    return () => window.removeEventListener("keydown", handleKeydown)
  }, [onClose])

  return (
    <aside className="issue-zoom-panel slide-up" aria-label="Issue detail">
      <div className="issue-detail-toolbar">
        {statusMenu}
        {reportAction}
        <button type="button" className="detail-panel-action" aria-label="Close issue detail" title="Close issue detail" onClick={onClose}>
          <X aria-hidden="true" strokeWidth={1.8} />
        </button>
      </div>
      <div className="issue-zoom-panel-body">{children}</div>
    </aside>
  )
}

function IssueDetailStatusMenu({ issue, onStatusChange }) {
  const [open, setOpen] = useState(false)
  const menuRef = useDismissiblePopover(open, () => setOpen(false))
  const labels = { archive: "Archive", complete: "Mark as Completed", open: "Reopen" }
  const actions = issueQuickActions(issue)
  if (actions.length === 0) return null

  function chooseAction(action) {
    setOpen(false)
    onStatusChange?.(issue, action.status)
  }

  return (
    <div className="issue-detail-status-menu" ref={menuRef}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Inbox item actions"
        className="detail-panel-action issue-detail-status-menu-trigger"
        onClick={() => setOpen((current) => !current)}
      >
        <MoreHorizontal aria-hidden="true" strokeWidth={1.8} />
      </button>

      <div
        className="issue-detail-status-menu-popover"
        hidden={!open}
        role="menu"
        aria-label="Inbox item status actions"
      >
        {actions.map((action) => (
          <button
            type="button"
            className={`issue-detail-status-menu-item is-${action.key}`}
            key={action.key}
            role="menuitem"
            onClick={() => chooseAction(action)}
          >
            <action.Icon aria-hidden="true" strokeWidth={1.8} />
            <span>{labels[action.key] || action.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

function IssueRelatedArtifacts({ relatedArtifacts = {} }) {
  const items = [
    ...(relatedArtifacts.personas || []),
    ...(relatedArtifacts.actions || []),
    ...(relatedArtifacts.concepts || [])
  ]
  if (items.length === 0) return null

  return (
    <section className="issue-detail-section issue-context-strip" aria-label="Related context">
      <h3 className="detail-label">Context</h3>
      <div className="issue-context-strip-items">
        {items.map((item) => <IssueContextChip item={item} key={item.key || item.title} />)}
      </div>
    </section>
  )
}

const issueContextChipKindLabels = {
  persona: "Persona",
  action: "User action",
  concept: "Concept"
}

function IssueContextChip({ item }) {
  const kind = issueContextChipKindLabels[item.type] || "Context"
  const content = (
    <>
      <span className="detail-list-thumb" aria-hidden="true">
        <IssueContextChipThumb item={item} />
      </span>
      <span className="detail-list-copy">
        <span className="detail-list-title">{item.title}</span>
        <span className="detail-list-meta">{kind}</span>
      </span>
      <ChevronRight aria-hidden="true" className="detail-list-chevron" strokeWidth={1.8} />
    </>
  )

  if (item.detailPath) {
    return <a className="detail-list-row issue-context-chip" href={item.detailPath} aria-label={`Open ${kind}: ${item.title}`}>{content}</a>
  }

  return <div className="detail-list-row issue-context-chip is-static">{content}</div>
}

function IssueContextChipThumb({ item }) {
  if (item.type === "persona") return <PersonaAvatar imageLoading="lazy" index={item.index} user={item.persona} />
  if (item.type === "action" && item.image?.url) return <img src={item.image.url} alt="" loading="lazy" />
  if (item.type === "action") return <Presentation strokeWidth={1.6} />

  return <Star strokeWidth={1.6} />
}

export function issueListCardToneStyle() {
  return {
    "--issue-card-accent": "#2de0ad",
    "--issue-card-background": "#15171b",
    "--issue-card-border": "rgba(255, 255, 255, 0.08)",
    "--issue-card-control-text": "#04120d",
    "--issue-card-foreground": "#f5f6f7",
    "--issue-card-muted": "#8e9499"
  }
}

export function IssueCard({ children, issue, metadata = null }) {
  const visual = issue?.visual || {}
  const visualStatus = visual.url ? "ready" : visual.status || "idle"
  const cardMetadata = metadata || issueDetailMetadata(issue)
  const className = [
    "issue-card",
    "is-detail",
    "focus-card issue-detail issue-detail-main",
    inboxIssueStatusClassName(issue)
  ].filter(Boolean).join(" ")

  return (
    <article className={className} style={issueListCardToneStyle()}>
      <div className={`issue-card-visual is-${visualStatus}`.trim()} aria-hidden="true">
        {visual.url ? (
          <img
            src={visual.url}
            alt=""
            aria-hidden="true"
            decoding="async"
            fetchPriority="high"
            loading="eager"
          />
        ) : (
          <div className="issue-card-visual-placeholder">
            {visualStatus === "generating" ? <LoadingOrb size={64} state="shaping" /> : <Image aria-hidden="true" strokeWidth={1.4} />}
            <span>{issueVisualPlaceholderLabel(visualStatus)}</span>
          </div>
        )}
      </div>

      <div className="issue-card-content">
        <h1 className="issue-card-title">{issue.title}</h1>
        {cardMetadata ? (
          <div className="issue-card-metadata">
            <InboxIssueStatusMark issue={issue} />
            <span>{cardMetadata}</span>
          </div>
        ) : null}
        <div className="issue-card-copy issue-description issue-action-surface">
          <p>{issue.description}</p>
        </div>

        {children}
      </div>
    </article>
  )
}

function issueVisualPlaceholderLabel(status) {
  if (status === "generating") return "Creating illustration"
  if (status === "failed") return "Illustration unavailable"

  return "Issue illustration"
}

function IssueComments({
  actionState,
  attachments,
  canComment,
  draft,
  issue,
  mentionableMembers = [],
  onAttachFiles,
  onDraftChange,
  onRemoveAttachment,
  onStartRecording,
  onStopRecording,
  onSubmit,
  recorderState
}) {
  const textareaRef = useRef(null)
  const [mentionState, setMentionState] = useState(null)
  const comments = issue.comments || []
  const members = useMemo(() => commentMentionMembers(mentionableMembers), [mentionableMembers])
  const submitting = actionState.status === "submitting"
  const stopping = recorderState.status === "stopping"
  const recording = recorderState.status === "recording" || stopping
  const canSubmit = canComment && !submitting && !recording && Boolean(draft.trim() || attachments.length > 0)
  const attachmentInputId = `issue-${issue.id}-comment-attachments`
  const mentionMenuId = `issue-${issue.id}-mention-options`
  const mentionOptions = mentionState ? commentMentionMatches(members, mentionState.query) : []
  const mentionMenuOpen = canComment && !submitting && mentionOptions.length > 0
  const activeMentionId = mentionMenuOpen ? `${mentionMenuId}-${mentionState.selectedIndex}` : undefined

  function closeMentionMenu() {
    setMentionState(null)
  }

  function updateMentionState(value, caretIndex) {
    const nextMention = commentMentionAtCaret(value, caretIndex)

    if (!nextMention) {
      closeMentionMenu()
      return
    }

    setMentionState((current) => ({
      ...nextMention,
      selectedIndex: Math.min(current?.selectedIndex || 0, Math.max(commentMentionMatches(members, nextMention.query).length - 1, 0))
    }))
  }

  function handleDraftChange(event) {
    const value = event.target.value

    onDraftChange(value)
    updateMentionState(value, event.target.selectionStart)
  }

  function handleDraftKeyDown(event) {
    if (!mentionMenuOpen) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      setMentionState((current) => current ? {
        ...current,
        selectedIndex: (current.selectedIndex + 1) % mentionOptions.length
      } : current)
      return
    }

    if (event.key === "ArrowUp") {
      event.preventDefault()
      setMentionState((current) => current ? {
        ...current,
        selectedIndex: (current.selectedIndex - 1 + mentionOptions.length) % mentionOptions.length
      } : current)
      return
    }

    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault()
      insertMention(mentionOptions[mentionState.selectedIndex] || mentionOptions[0])
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      closeMentionMenu()
    }
  }

  function handleDraftKeyUp(event) {
    if ([ "ArrowDown", "ArrowUp", "Enter", "Tab", "Escape" ].includes(event.key)) return

    updateMentionState(event.currentTarget.value, event.currentTarget.selectionStart)
  }

  function insertMention(member) {
    if (!member || !mentionState) return

    const handle = commentMentionHandle(member)
    if (!handle) return

    const nextValue = `${draft.slice(0, mentionState.start)}@${handle} ${draft.slice(mentionState.end)}`
    const nextCaret = mentionState.start + handle.length + 2

    onDraftChange(nextValue)
    closeMentionMenu()
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus()
      textareaRef.current?.setSelectionRange(nextCaret, nextCaret)
    })
  }

  return (
    <aside className="focus-card issue-collaboration" aria-labelledby={`issue-${issue.id}-comments-title`}>
      <div className="issue-collaboration-heading">
        <div id={`issue-${issue.id}-comments-title`} className="detail-label">COMMENTS</div>
      </div>

      <div className="issue-comment-body">
        <div className={`issue-comment-list ${comments.length ? "" : "is-empty"}`.trim()}>
          {comments.length ? (
            comments.map((comment) => <IssueComment comment={comment} key={comment.id} />)
          ) : (
            <p className="issue-comment-empty">No comments yet.</p>
          )}
        </div>

        <form className="issue-comment-form" onSubmit={onSubmit}>
          <label className="issue-comment-field">
            <span className="sr-only">Comment on this issue</span>
            <textarea
              ref={textareaRef}
              value={draft}
              aria-activedescendant={activeMentionId}
              aria-autocomplete="list"
              aria-controls={mentionMenuOpen ? mentionMenuId : undefined}
              aria-expanded={mentionMenuOpen}
              disabled={!canComment || submitting}
              maxLength={2000}
              rows={4}
              placeholder={canComment ? "Comment on this issue" : "Sign in with GitHub to comment"}
              onBlur={() => window.setTimeout(closeMentionMenu, 120)}
              onChange={handleDraftChange}
              onClick={(event) => updateMentionState(event.currentTarget.value, event.currentTarget.selectionStart)}
              onKeyDown={handleDraftKeyDown}
              onKeyUp={handleDraftKeyUp}
            />
          </label>

          {mentionMenuOpen ? (
            <div className="issue-comment-mention-menu" id={mentionMenuId} role="listbox" aria-label="Mention workspace member">
              {mentionOptions.map((member, index) => (
                <button
                  type="button"
                  className={index === mentionState.selectedIndex ? "is-active" : ""}
                  id={`${mentionMenuId}-${index}`}
                  key={member.key}
                  role="option"
                  aria-selected={index === mentionState.selectedIndex}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => insertMention(member)}
                >
                  <span className="issue-comment-mention-avatar" aria-hidden="true">
                    {member.avatarUrl ? <img src={member.avatarUrl} alt="" /> : commentMentionInitial(member)}
                  </span>
                  <span className="issue-comment-mention-copy">
                    <strong>{member.name || member.githubLogin}</strong>
                    <small>@{commentMentionHandle(member)}</small>
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {attachments.length ? (
            <div className="issue-comment-draft-attachments" aria-label="Pending attachments">
              {attachments.map((attachment) => (
                <DraftAttachment attachment={attachment} key={attachment.id} onRemove={onRemoveAttachment} />
              ))}
            </div>
          ) : null}

          {actionState.error || recorderState.error ? (
            <p className="issue-report-message" role="alert">{actionState.error || recorderState.error}</p>
          ) : null}

          <div className="issue-comment-toolbar">
            {recording ? (
              <button
                type="button"
                className="issue-comment-tool-button is-recording"
                disabled={!canComment || submitting || stopping}
                onClick={onStopRecording}
              >
                <Square aria-hidden="true" strokeWidth={1.8} />
                <span>{stopping ? "Attaching" : "Stop"}</span>
              </button>
            ) : (
              <button
                type="button"
                className="issue-comment-tool-button"
                disabled={!canComment || submitting || stopping}
                onClick={onStartRecording}
              >
                <Mic aria-hidden="true" strokeWidth={1.8} />
                <span>Record</span>
              </button>
            )}

            <label className={`issue-comment-tool-button ${!canComment || submitting ? "is-disabled" : ""}`.trim()} htmlFor={attachmentInputId}>
              <Paperclip aria-hidden="true" strokeWidth={1.8} />
              <span>Attach</span>
              <input
                id={attachmentInputId}
                type="file"
                multiple
                disabled={!canComment || submitting}
                onChange={onAttachFiles}
              />
            </label>

            <button
              type="submit"
              className="issue-comment-submit"
              disabled={!canSubmit}
            >
              <SendHorizontal aria-hidden="true" strokeWidth={1.8} />
              <span>{submitting ? "Posting" : "Post"}</span>
            </button>
          </div>
        </form>
      </div>
    </aside>
  )
}

export function commentMentionAtCaret(value = "", caretIndex = 0) {
  const caret = Math.max(0, Math.min(caretIndex, value.length))
  const beforeCaret = value.slice(0, caret)
  const match = beforeCaret.match(/(^|\s)@([A-Za-z0-9_-]*)$/)
  if (!match) return null

  const query = match[2] || ""
  const start = caret - query.length - 1

  return {
    query,
    start,
    end: caret,
    selectedIndex: 0
  }
}

export function commentMentionMembers(members = []) {
  const seen = new Set()

  return members
    .map((member) => ({
      avatarUrl: member.avatarUrl,
      githubLogin: cleanCommentMentionText(member.githubLogin),
      key: String(member.id || member.githubLogin || member.name || ""),
      name: cleanCommentMentionText(member.name)
    }))
    .filter((member) => commentMentionHandle(member))
    .filter((member) => {
      const key = commentMentionHandle(member).toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .sort((left, right) => commentMentionLabel(left).localeCompare(commentMentionLabel(right)))
}

export function commentMentionMatches(members = [], query = "") {
  const normalizedQuery = query.toLowerCase()

  return members
    .filter((member) => {
      if (!normalizedQuery) return true

      return commentMentionHandle(member).toLowerCase().includes(normalizedQuery) ||
        commentMentionLabel(member).toLowerCase().includes(normalizedQuery)
    })
    .slice(0, 6)
}

function commentMentionHandle(member = {}) {
  return cleanCommentMentionText(member.githubLogin) || commentMentionFallbackHandle(member.name)
}

function commentMentionLabel(member = {}) {
  return member.name || member.githubLogin || ""
}

function commentMentionInitial(member = {}) {
  return commentMentionLabel(member).slice(0, 1).toUpperCase() || "@"
}

function commentMentionFallbackHandle(value = "") {
  return cleanCommentMentionText(value)
    ?.toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-|-$/g, "")
}

function cleanCommentMentionText(value) {
  return String(value || "").trim() || null
}

function IssueComment({ comment }) {
  const author = comment.author || {}
  const authorName = author.name || author.githubLogin || "Teammate"
  const attachments = comment.attachments || []
  const voiceComment = comment.kind === "voice_note"

  return (
    <article className="issue-comment">
      <div className="issue-comment-author-row">
        <span className="issue-comment-avatar" aria-hidden="true">
          {author.avatarUrl ? <img src={author.avatarUrl} alt="" /> : authorName.slice(0, 1).toUpperCase()}
        </span>
        <span className="issue-comment-author">
          <strong>{authorName}</strong>
          <small>{commentAgeLabel(comment)}</small>
        </span>
      </div>
      {comment.body ? <p>{comment.body}</p> : null}
      {attachments.length ? (
        <CommentAttachments attachments={attachments} voiceComment={voiceComment} />
      ) : null}
      {voiceComment ? (
        <CommentVoiceTranscription comment={comment} />
      ) : null}
    </article>
  )
}

function DraftAttachment({ attachment, onRemove }) {
  const Icon = attachmentIcon(attachment)

  return (
    <span className="issue-comment-draft-attachment">
      <Icon aria-hidden="true" strokeWidth={1.8} />
      <span>
        <strong>{attachment.name}</strong>
        <small>{attachmentSizeLabel(attachment.byteSize)}</small>
      </span>
      <button type="button" aria-label={`Remove ${attachment.name}`} onClick={() => onRemove(attachment.id)}>
        <X aria-hidden="true" strokeWidth={1.8} />
      </button>
    </span>
  )
}

function CommentAttachments({ attachments, voiceComment }) {
  return (
    <div className={`issue-comment-attachments ${voiceComment ? "has-voice-note" : ""}`.trim()}>
      {attachments.map((attachment) => (
        <CommentAttachment attachment={attachment} key={attachment.id || attachment.url || attachment.filename} />
      ))}
    </div>
  )
}

function CommentAttachment({ attachment }) {
  if (attachment.mediaKind === "audio") {
    return (
      <audio className="issue-comment-audio" src={attachment.url} controls preload="metadata" />
    )
  }

  if (attachment.mediaKind === "image") {
    return (
      <a className="issue-comment-image" href={attachment.url} target="_blank" rel="noopener noreferrer">
        <img src={attachment.url} alt={attachment.filename || "Comment attachment"} loading="lazy" />
      </a>
    )
  }

  return (
    <a className="issue-comment-file" href={attachment.url} target="_blank" rel="noopener noreferrer">
      <FileText aria-hidden="true" strokeWidth={1.8} />
      <span>
        <strong>{attachment.filename || "Attachment"}</strong>
        <small>{attachmentSizeLabel(attachment.byteSize)}</small>
      </span>
    </a>
  )
}

function CommentVoiceTranscription({ comment }) {
  if (comment.voiceTranscriptionStatus === "ready" && comment.voiceTranscription) {
    return (
      <div className="issue-transcription">
        <div className="detail-label">Transcription</div>
        <p className="issue-transcription-text">{comment.voiceTranscription}</p>
      </div>
    )
  }

  if (comment.voiceTranscriptionStatus === "transcribing") {
    return (
      <div className="issue-transcription">
        <div className="detail-label">Transcription</div>
        <p className="issue-transcription-muted">
          <LoadingOrb state="listening" />
          <span>Transcribing voice note...</span>
        </p>
      </div>
    )
  }

  if (comment.voiceTranscriptionStatus === "failed") {
    return (
      <div className="issue-transcription">
        <div className="detail-label">Transcription</div>
        <p className="issue-report-message" role="alert">{comment.voiceTranscriptionError || "Could not transcribe this recording."}</p>
      </div>
    )
  }

  return null
}

export function IssuePullRequestAction({ issue, onGenerate, onRetryPublication, onStop, onCredentialsReady, pollingInterrupted = false, publicationRetrying = false }) {
  const [credentialSetupOpen, setCredentialSetupOpen] = useState(false)
  const [clock, setClock] = useState(() => Date.now())
  const credentialSetupRef = useRef(null)
  const pullRequest = issue.pullRequest
  const automaticPublicationRetrying = pullRequestAutomaticRetryPending(pullRequest, clock)
  const generatingNow = pullRequestPollingActive(pullRequest, clock)

  useEffect(() => {
    setClock(Date.now())
    if (!generatingNow) return undefined

    const interval = window.setInterval(() => setClock(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [generatingNow, pullRequest?.automaticRetryUntil, pullRequest?.startedAt])

  if (pullRequest?.credentialSetup) credentialSetupRef.current = pullRequest.credentialSetup
  if (!pullRequest) return null

  const { status, ready, generating, retryPublication, disabled } = pullRequestActionState(pullRequest, Boolean(onGenerate), clock)
  const completedWithoutPullRequest = completedNoPullRequestOutcome(pullRequest)
  const buttonLabel = retryPublication ? "Retry publication" : pullRequestButtonLabel(status, completedWithoutPullRequest)
  const retryingPublication = publicationRetrying || automaticPublicationRetrying
  const actionDisabled = disabled || retryingPublication || (retryPublication && !onRetryPublication)
  const buttonTitle = actionDisabled ? (pullRequest.disabledReason || buttonLabel) : buttonLabel
  const canStop = generating && Boolean(pullRequest.stopPath) && Boolean(onStop)
  const streamEvents = pullRequest.streamEvents || []
  const showStream = generating || streamEvents.length > 0
  const timingPullRequest = automaticPublicationRetrying ? { ...pullRequest, finishedAt: null } : pullRequest
  const timingLabel = pullRequestTimingLabel(timingPullRequest, streamEvents, clock)
  const streamLines = showStream ? pullRequestTranscriptLines(streamEvents, {
    status: automaticPublicationRetrying
      ? "Retrying pull request publication."
      : (generating ? "Running Codex." : "Pull request generation log.")
  }) : []

  return (
    <div className={`issue-pr-action is-${status}`.trim()} role="group" aria-label="Pull request generation">
      {status === "failed" && !automaticPublicationRetrying ? (
        <div className="issue-pr-failure" role="alert">
          <div className="issue-pr-failure-heading">
            <CircleAlert aria-hidden="true" strokeWidth={1.8} />
            <div>
              <strong>{retryPublication ? "Pull request publication failed" : "Pull request not created"}</strong>
              <p>{pullRequest.error || "The generation run evaluated the candidate approaches but ended before a pull request was created."}</p>
            </div>
          </div>

          {pullRequest.agentRunPath ? (
            <a className="issue-pr-run-link" href={pullRequest.agentRunPath}>View run details</a>
          ) : null}
        </div>
      ) : null}

      <div className="issue-pr-controls">
        {ready ? (
          <a
            className="issue-pr-primary"
            href={pullRequest.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <GitPullRequest aria-hidden="true" strokeWidth={1.8} />
            <span>{pullRequest.number ? `Review PR #${pullRequest.number}` : "Review PR"}</span>
            <ExternalLink aria-hidden="true" strokeWidth={1.8} />
          </a>
        ) : (
          <button
            type="button"
            className={`issue-pr-primary ${generating || retryingPublication ? "is-generating" : ""}`.trim()}
            disabled={actionDisabled}
            title={buttonTitle}
            onClick={() => retryPublication ? onRetryPublication(issue) : onGenerate(issue)}
          >
            {generating || retryingPublication ? <LoadingOrb state="composing" theme="light" /> : <GitPullRequest aria-hidden="true" strokeWidth={1.8} />}
            <span>{retryingPublication ? "Retrying publication…" : buttonLabel}</span>
          </button>
        )}

        {ready && (pullRequest.additions != null || pullRequest.deletions != null) ? (
          <span className="issue-pr-diff-stats" title="Lines changed in this pull request">
            <span className="issue-pr-diff-stat is-added">+{Number(pullRequest.additions) || 0}</span>
            <span className="issue-pr-diff-stat is-removed">-{Number(pullRequest.deletions) || 0}</span>
          </span>
        ) : null}

        {canStop ? (
          <button
            type="button"
            className="issue-pr-secondary is-stop"
            onClick={() => onStop(issue)}
          >
            <Square aria-hidden="true" strokeWidth={1.8} />
            <span>Stop run</span>
          </button>
        ) : null}
      </div>

      {pollingInterrupted ? (
        <p className="issue-report-message issue-pr-polling-warning" role="status">Updates interrupted; retrying</p>
      ) : null}
      {timingLabel ? <p className="issue-pr-timing" role="status">{timingLabel}</p> : null}

      {completedWithoutPullRequest ? (
        <p className="issue-report-message issue-pr-diagnosis" role="status">
          <strong>{noPullRequestOutcomeLabels[pullRequest.outcome]}</strong>
          {pullRequest.diagnosis ? ` ${pullRequest.diagnosis}` : null}
        </p>
      ) : null}
      {!ready && !generating && !pullRequest.canGenerate && pullRequest.disabledReason ? (
        pullRequest.disabledCode === "credential_selection_required" && pullRequest.credentialSetup?.pagePath ? (
          <div className="issue-pr-credential-cta">
            <p className="issue-report-message">{pullRequest.disabledReason}</p>
            <a
              className="issue-pr-secondary"
              href={pullRequest.credentialSetup.pagePath}
            >
              <span>Select Codex credential</span>
              <ArrowRight aria-hidden="true" strokeWidth={1.8} />
            </a>
          </div>
        ) : [ "credential_required", "credential_reauthentication_required" ].includes(pullRequest.disabledCode) && pullRequest.credentialSetup ? (
          <div className="issue-pr-credential-cta">
            <p className="issue-report-message">{pullRequest.disabledReason}</p>
            <button
              type="button"
              className="issue-pr-secondary"
              onClick={() => setCredentialSetupOpen(true)}
            >
              <span>{pullRequest.disabledCode === "credential_reauthentication_required" ? "Reconnect Codex" : "Set up Codex credentials"}</span>
              <ArrowRight aria-hidden="true" strokeWidth={1.8} />
            </button>
          </div>
        ) : (
          <p className="issue-report-message">{pullRequest.disabledReason}</p>
        )
      ) : null}
      {showStream ? (
        <>
          <PullRequestWorkflowGraph events={streamEvents} generating={generating} pullRequest={pullRequest} />
          <details className="issue-pr-technical-log">
            <summary>Technical log ({streamEvents.length} {streamEvents.length === 1 ? "event" : "events"})</summary>
            <AgentActivityFeed
              className="issue-thinking-stream issue-pr-stream"
              events={streamEvents}
              generating={generating}
              lines={streamLines}
              transcript
            />
          </details>
        </>
      ) : null}
      {credentialSetupOpen ? (
        <CredentialSetupModal
          credentialSetup={pullRequest.credentialSetup || credentialSetupRef.current}
          onClose={() => setCredentialSetupOpen(false)}
          onCredentialsReady={onCredentialsReady}
          onGenerate={() => {
            setCredentialSetupOpen(false)
            onGenerate(issue)
          }}
        />
      ) : null}
    </div>
  )
}

function IssueReportAction({ issue, onGenerate }) {
  const reportStatus = issue.reportStatus || "idle"

  if (reportStatus === "ready") {
    return (
      <div className="issue-report-action issue-report-ready" role="group" aria-label="Issue report">
        <a
          href={issue.reportPath}
          target="issueReport"
          rel="noopener"
          className="detail-panel-action issue-report-view-link"
          aria-label="View report"
          title="View report"
          onClick={(event) => openIssueReportPopup(event, issue.reportPath)}
        >
          <FileText aria-hidden="true" strokeWidth={1.8} />
        </a>
      </div>
    )
  }

  const generating = reportStatus === "generating"

  return (
    <div className="issue-report-action issue-report-generator" role="group" aria-label="Issue report">
      <button
        type="button"
        className={`issue-report-link ${generating ? "is-generating" : ""}`.trim()}
        disabled={generating}
        onClick={() => onGenerate(issue)}
      >
        {generating ? <LoadingOrb state="composing" /> : <Presentation aria-hidden="true" strokeWidth={1.8} />}
        <span className="issue-report-link-label">{reportButtonLabel(reportStatus)}</span>
      </button>
      {reportStatus === "failed" && issue.reportError ? (
        <p className="issue-report-message" role="alert">{issue.reportError}</p>
      ) : null}
    </div>
  )
}

export function openIssueReportPopup(event, reportPath) {
  if (!reportPath) return
  if (typeof window === "undefined") return

  event.preventDefault()

  const width = 980
  const height = 760
  const screenLeft = window.screenLeft ?? window.screenX ?? 0
  const screenTop = window.screenTop ?? window.screenY ?? 0
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || width
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || height
  const left = screenLeft + Math.max(0, (viewportWidth - width) / 2)
  const top = screenTop + Math.max(0, (viewportHeight - height) / 2)
  const features = `popup=yes,width=${width},height=${height},left=${Math.round(left)},top=${Math.round(top)},noopener,noreferrer,resizable=yes,scrollbars=yes`

  const popup = window.open(reportPath, "issueReport", features)
  popup?.focus?.()
}

function reportButtonLabel(reportStatus) {
  if (reportStatus === "generating") return "Generating issue report…"
  if (reportStatus === "failed") return "Retry issue report"

  return "View issue report"
}

function completedNoPullRequestOutcome(pullRequest = {}) {
  return pullRequest.status === "idle" &&
    Boolean(pullRequest.finishedAt) &&
    noPullRequestOutcomes.has(pullRequest.outcome)
}

function pullRequestButtonLabel(status, completedWithoutPullRequest = false) {
  if (status === "generating") return "Generating PR"
  if (status === "failed" || completedWithoutPullRequest) return "Retry PR"

  return "Generate PR"
}

function issueDetailMetadata(issue) {
  return [
    issue?.severity,
    issue?.status === inboxIssueStatuses.open ? null : inboxIssueStatusLabel(issue?.status),
    stackLayerLabelForIssue(issue),
    issue?.businessDomainName,
    issueUpdatedAtLabel(issue)
  ].filter(Boolean).join(" · ")
}

function issueRelatedArtifacts(issue, { concept = null, contextActionListPath = null, contextOverview = null, contextPersonaListPath = null, contextStoryboards = [] } = {}) {
  return {
    personas: issueRelatedPersonas(issue, contextOverview, contextPersonaListPath),
    actions: issueRelatedActions(issue, contextOverview, contextActionListPath, contextStoryboards),
    concepts: issueRelatedConcepts(issue, concept)
  }
}

function issueRelatedPersonas(issue, contextOverview, contextPersonaListPath) {
  const references = issueArtifactKeySet(issue?.personaKeys || issue?.persona_keys)
  if (references.size === 0) return []

  return limitedArray(contextOverview?.users)
    .map((persona, index) => ({ persona, index }))
    .filter(({ persona }) => references.has(contextPersonaKey(persona)))
    .map(({ persona, index }) => {
      const key = contextPersonaKey(persona)

      return {
        key,
        type: "persona",
        title: personaDisplayName(persona),
        description: personaCardEssence(persona),
        detailPath: persona.detailPath || contextPersonaDetailPath(contextPersonaListPath, key),
        index,
        persona
      }
    })
}

function issueRelatedActions(issue, contextOverview, contextActionListPath, contextStoryboards = []) {
  const references = issueArtifactKeySet(issue?.actionKeys || issue?.action_keys)
  if (references.size === 0) return []

  return limitedArray(contextOverview?.actions)
    .filter((action) => references.has(contextActionKey(action)))
    .map((action) => {
      const key = contextActionKey(action)

      return {
        key,
        type: "action",
        title: action.label || action.name || key,
        description: action.description,
        action,
        detailPath: action.detailPath || contextActionDetailPath(contextActionListPath, key),
        image: issueActionImage(action, contextStoryboards),
        personaUsers: actionPersonaUsers(action, limitedArray(contextOverview?.users))
      }
    })
}

function issueRelatedConcepts(issue, concept) {
  const issueKey = String(issue?.key || "")
  if (!issueKey) return []

  return limitedArray(concept?.concepts?.items).flatMap((conceptItem) => {
    const conceptKey = conceptItem.key || conceptItem.title
    const conceptTitle = conceptItem.displayTitle || conceptItem.title || conceptKey

    return limitedArray(conceptItem.nextMoves).filter((move) => issueMoveInboxIssueKey(move) === issueKey).map((move) => ({
      key: `${conceptKey || conceptTitle}:${move.key || move.title || issueKey}`,
      type: "concept",
      concept: conceptItem,
      detailPath: conceptItem.detailPath || null,
      title: conceptTitle
    }))
  })
}

function issueActionImage(action = {}, contextStoryboards = []) {
  const actionKey = contextActionKey(action)
  const storyboard = limitedArray(contextStoryboards).find((candidate) => storyboardMatchesAction(candidate, action, actionKey))
  const frame = limitedArray(storyboard?.uiFrames)[0] || limitedArray(storyboard?.frames)[0]
  const url = frame?.thumbnailUrl || frame?.url
  if (!url) return null

  const title = action.label || action.name || storyboard?.actionLabel || "User action"

  return {
    kind: "action",
    url,
    alt: frame.alt || `${title} product UI frame`
  }
}

function storyboardMatchesAction(storyboard = {}, action = {}, actionKey = "") {
  const storyboardKeys = new Set([
    storyboard.actionKey,
    storyboard.actionKey ? contextActionKey({ key: storyboard.actionKey }) : null,
    storyboard.actionLabel ? contextActionKey({ label: storyboard.actionLabel }) : null
  ].filter(Boolean))
  const actionKeys = [
    actionKey,
    action.key,
    contextActionKey(action)
  ].filter(Boolean)

  return actionKeys.some((key) => storyboardKeys.has(key))
}

function issueMoveInboxIssueKey(move = {}) {
  return String(move?.inboxIssue?.key || move?.inboxIssueKey || move?.inbox_issue_key || "")
}

function issueArtifactKeySet(value) {
  return new Set(limitedArray(value).map((item) => String(item || "").trim()).filter(Boolean))
}

function limitedArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : []
}

function contextActionDetailPath(listPath, actionKey) {
  if (!listPath || !actionKey) return null

  return `${listPath.replace(/\/$/, "")}/actions/${encodeURIComponent(actionKey)}`
}

function contextPersonaDetailPath(listPath, personaKey) {
  if (!listPath || !personaKey) return null

  return `${listPath.replace(/\/$/, "")}/personas/${encodeURIComponent(personaKey)}`
}

function stackLayerLabelForIssue(issue) {
  return stackComponentLayerLabels[issue?.stackComponentCategory] || null
}

function issueUpdatedAtLabel(issue) {
  const updatedAt = issue?.updatedAtInWords || relativeTimeLabel(issue?.updatedAt)

  return updatedAt ? `Updated ${updatedAt}` : ""
}

function issueLocalStateFromUpdate(updatedIssue, nextIssue, { keys = [] } = {}) {
  if (!updatedIssue) return {}

  const state = {}

  Array.from(new Set([ ...localIssueStateKeys, ...keys ])).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(updatedIssue, key)) return
    if (nextIssue?.[key] === undefined) return

    state[key] = nextIssue[key]
  })

  return state
}

function pruneLocalIssueStates(currentStates, sourceIssues) {
  const sourceById = new Map(sourceIssues.map((issue) => [String(issue.id), issue]))
  const nextStates = {}
  let changed = false

  Object.entries(currentStates).forEach(([issueId, state]) => {
    const sourceIssue = sourceById.get(issueId)
    if (!sourceIssue) {
      changed = true
      return
    }

    const nextState = {}
    Object.entries(state).forEach(([key, value]) => {
      if (Object.is(sourceIssue[key], value)) {
        changed = true
        return
      }

      nextState[key] = value
    })

    if (Object.keys(nextState).length > 0) {
      nextStates[issueId] = nextState
    } else {
      changed = true
    }
  })

  return changed ? nextStates : currentStates
}

function localIssueStateEqual(currentState = {}, nextState = {}) {
  const currentKeys = Object.keys(currentState)
  const nextKeys = Object.keys(nextState)
  if (currentKeys.length !== nextKeys.length) return false

  return nextKeys.every((key) => Object.is(currentState[key], nextState[key]))
}

function issueQuickActions(issue) {
  if (!issue) return []

  if (isClosedInboxIssue(issue)) {
    return [
      {
        Icon: InboxIcon,
        key: "open",
        label: "Mark as open",
        status: inboxIssueStatuses.open,
        tooltip: "Move this item back to the inbox"
      }
    ]
  }

  return [
    {
      Icon: Check,
      key: "complete",
      label: "Mark as complete",
      status: inboxIssueStatuses.done,
      tooltip: "Mark complete and remove from the inbox"
    },
    {
      Icon: Archive,
      key: "archive",
      label: "Archive",
      status: inboxIssueStatuses.archived,
      tooltip: "Archive and remove from the inbox"
    }
  ]
}

function browserRecordingSupported() {
  return typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia) &&
    typeof MediaRecorder !== "undefined"
}

function preferredRecorderMimeType() {
  if (typeof MediaRecorder === "undefined" || typeof MediaRecorder.isTypeSupported !== "function") return null

  return [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4"
  ].find((mimeType) => MediaRecorder.isTypeSupported(mimeType))
}

function stopRecordingTracks(recording) {
  recording?.stream?.getTracks().forEach((track) => track.stop())
}

function recordedVoiceFile(blob) {
  const filename = recordingFilename(blob)

  if (typeof File === "undefined") {
    return Object.assign(blob, { name: filename })
  }

  return new File([blob], filename, { type: blob.type || "audio/webm" })
}

function commentAttachmentEntry(file, source) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    file,
    source,
    name: file.name || recordingFilename(file),
    contentType: file.type || "application/octet-stream",
    byteSize: file.size || 0
  }
}

function attachmentIcon(attachment) {
  if (attachment.source === "voice" || attachment.contentType?.startsWith("audio/")) return Mic
  if (attachment.contentType?.startsWith("image/")) return Image

  return FileText
}

function recordingFilename(recording) {
  if (recording.type?.includes("mp4")) return "voice-note.m4a"
  if (recording.type?.includes("wav")) return "voice-note.wav"

  return "voice-note.webm"
}

function attachmentSizeLabel(byteSize) {
  const size = Number(byteSize) || 0
  if (size >= 1_000_000) return `${(size / 1_000_000).toFixed(1)} MB`
  if (size >= 1_000) return `${Math.round(size / 1_000)} KB`

  return `${size} B`
}

function commentAgeLabel(comment) {
  return relativeAgeLabel(comment?.createdAt, comment?.createdAtInWords)
}

function relativeAgeLabel(isoString, fallback) {
  const liveLabel = relativeTimeLabel(isoString)
  if (liveLabel) return liveLabel

  const normalizedFallback = normalizeRelativeTimeFallback(fallback)
  if (normalizedFallback) return normalizedFallback

  return ""
}

function normalizeRelativeTimeFallback(value) {
  const label = String(value || "").trim()
  if (!label) return ""

  const compactDayMatch = label.match(/^(\d+)\s*d\s+ago$/i)
  if (compactDayMatch) {
    const days = Math.max(1, Number(compactDayMatch[1]) || 1)
    return `${days} ${days === 1 ? "day" : "days"} ago`
  }

  if (/^less than a minute ago$/i.test(label)) return "a few seconds ago"
  if (/^1 minute ago$/i.test(label)) return "a minute ago"

  return label.replace(/\s+ago$/i, " ago")
}

function relativeTimeLabel(isoString) {
  if (!isoString) return ""

  const date = new Date(isoString)
  if (Number.isNaN(date.valueOf())) return ""

  const diffSeconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000))
  if (diffSeconds < 60) return "a few seconds ago"
  if (diffSeconds < 120) return "a minute ago"

  const diffMinutes = Math.floor(diffSeconds / 60)
  if (diffMinutes < 60) return `${diffMinutes} ${diffMinutes === 1 ? "minute" : "minutes"} ago`

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`

  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`
}
